import {BaseGrid} from '@framewerx/kendo-angular-grid';
import {FilterableSettings} from '@progress/kendo-angular-grid';

let isPatched = false;
const GRID_FILTER_BUTTON_BOUND = '__frxGridFilterButtonCompatBound';
let globalToolbarCompatBound = false;

function hasRowFilterMode(filterable: FilterableSettings | undefined | null): boolean {
  if (filterable == null || filterable === false) {
    return false;
  }

  if (typeof filterable === 'string') {
    return filterable.toLowerCase().includes('row');
  }

  if (typeof filterable === 'object') {
    const mode = (filterable as { mode?: string }).mode;
    return typeof mode === 'string' ? mode.toLowerCase().includes('row') : true;
  }

  return !!filterable;
}

function normalizeFilterModeByViewport(instance: any): void {
  const rowEnabled = hasRowFilterMode(instance?.filterable) || !!instance?.filterRowEnabled;
  instance.filterRowEnabled = rowEnabled;
  instance.filterable = rowEnabled
    ? (window.innerWidth > 992 ? 'menu, row' : 'menu')
    : false;
}

function getGridContainer(instance: any): HTMLElement | null {
  const candidates = [
    instance?.kendoTable?.wrapper,
    instance?.kendoTable?.wrapper?.nativeElement,
    instance?.kendoTable?.element,
    instance?.kendoTable?.element?.nativeElement,
  ];

  for (const candidate of candidates) {
    if (!(candidate instanceof HTMLElement)) {
      continue;
    }

    if (candidate.classList.contains('k-grid')) {
      return candidate;
    }

    const childGrid = candidate.querySelector('.k-grid');
    if (childGrid instanceof HTMLElement) {
      return childGrid;
    }

    return candidate;
  }

  return null;
}

function getNormalizedButtonText(button: HTMLButtonElement): string {
  return (button.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function isHiddenCompatButton(button: HTMLButtonElement): boolean {
  return button.classList.contains('hidden-table-menu-item-trigger')
    || button.getAttribute('aria-hidden') === 'true';
}

function isGridToolbarFilterButton(button: HTMLButtonElement): boolean {
  const toolbar = button.closest('.k-grid-toolbar');
  if (!toolbar || isHiddenCompatButton(button)) {
    return false;
  }

  const normalizedText = getNormalizedButtonText(button);
  if (normalizedText.includes('filter')) {
    return true;
  }

  const title = (button.getAttribute('title') || '').toLowerCase();
  if (title.includes('filter')) {
    return true;
  }

  const ariaLabel = (button.getAttribute('aria-label') || '').toLowerCase();
  if (ariaLabel.includes('filter')) {
    return true;
  }

  if (button.querySelector('.k-svg-i-filter, .k-i-filter, .fa-filter')) {
    return true;
  }

  const hasAnyIcon = !!button.querySelector('.k-icon, .k-svg-icon, svg');
  if (!hasAnyIcon || normalizedText.length > 0) {
    return false;
  }

  const siblingButtons = Array
    .from((button.parentElement || toolbar).querySelectorAll('button'))
    .filter((node) => !isHiddenCompatButton(node as HTMLButtonElement)) as HTMLButtonElement[];
  const index = siblingButtons.indexOf(button);
  return index >= 0 && index <= 1;
}

function ensureFilterButtonLabel(button: HTMLButtonElement): void {
  if (button.querySelector('.frx-grid-filter-label')) {
    return;
  }

  const hasFilterText = getNormalizedButtonText(button).includes('filter');
  if (hasFilterText) {
    return;
  }

  const hasIconOnlyContent = getNormalizedButtonText(button).length === 0
    && !!button.querySelector('.k-icon, .k-svg-icon, svg');
  if (!hasIconOnlyContent) {
    return;
  }

  const label = document.createElement('span');
  label.className = 'ms-1 frx-grid-filter-label';
  label.textContent = 'Filter';
  button.appendChild(label);
}

function syncToolbarFilterButtonState(container: ParentNode): void {
  const buttons = container.querySelectorAll('button');

  buttons.forEach((buttonNode) => {
    const button = buttonNode as HTMLButtonElement;
    if (!isGridToolbarFilterButton(button)) {
      return;
    }

    ensureFilterButtonLabel(button);

    const grid = button.closest('.k-grid');
    const rowFilterVisible = !!grid?.querySelector('.k-filter-row');
    if (rowFilterVisible) {
      button.classList.add('k-selected');
      button.setAttribute('aria-pressed', 'true');
    } else {
      button.classList.remove('k-selected');
      button.setAttribute('aria-pressed', 'false');
    }
  });
}

function syncAllToolbarFilterButtonState(): void {
  syncToolbarFilterButtonState(document);
}

function bindGlobalToolbarFilterButtonCompat(): void {
  if (globalToolbarCompatBound) {
    return;
  }

  const clickHandler = (event: Event): void => {
    const target = event.target as Element | null;
    const button = target?.closest('button') as HTMLButtonElement | null;
    if (!button || !isGridToolbarFilterButton(button)) {
      return;
    }

    window.setTimeout(() => syncAllToolbarFilterButtonState(), 0);
  };

  document.addEventListener('click', clickHandler, true);

  const mutationObserver = new MutationObserver(() => {
    window.setTimeout(() => syncAllToolbarFilterButtonState(), 0);
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.setTimeout(() => syncAllToolbarFilterButtonState(), 0);
  globalToolbarCompatBound = true;
}

function bindToolbarFilterButtonCompat(instance: any): void {
  const container = getGridContainer(instance);
  if (container == null) {
    return;
  }

  if ((container as any)[GRID_FILTER_BUTTON_BOUND] === true) {
    return;
  }

  const clickHandler = (event: Event): void => {
    const target = event.target as Element | null;
    const button = target?.closest('button') as HTMLButtonElement | null;
    if (!button || !isGridToolbarFilterButton(button)) {
      return;
    }

    window.setTimeout(() => {
      normalizeFilterModeByViewport(instance);
      syncAllToolbarFilterButtonState();
    }, 0);
  };

  container.addEventListener('click', clickHandler, true);
  (container as any)[GRID_FILTER_BUTTON_BOUND] = true;
}

/**
 * Patches Framewerx BaseGrid so filtering is off by default unless
 * the caller explicitly opts into row filtering.
 */
export function patchFramewerxKendoAngularGridFilterDefault(): void {
  if (isPatched) {
    return;
  }

  const baseGridPrototype = (BaseGrid as any)?.prototype;
  const originalNgOnInit = baseGridPrototype?.ngOnInit;
  const originalNgAfterViewInit = baseGridPrototype?.ngAfterViewInit;
  const originalNgOnChanges = baseGridPrototype?.ngOnChanges;
  const originalOnResize = baseGridPrototype?.onResize;

  if (typeof originalNgOnInit !== 'function') {
    return;
  }

  baseGridPrototype.onResize = function (smallFilterMode = 'menu', largeFilterMode = 'menu, row'): void {
    if (typeof originalOnResize === 'function') {
      originalOnResize.apply(this, [smallFilterMode, largeFilterMode]);
    }

    if (window.innerWidth > 992) {
      this.columnMenu = this.originalColumnMenu;
      this.filterable = this.filterRowEnabled ? largeFilterMode : false;
    } else {
      this.columnMenu = false;
      this.filterable = this.filterRowEnabled ? smallFilterMode : false;
    }
  };

  baseGridPrototype.toggleFilterRow = function (smallFilterMode = 'menu', largeFilterMode = 'menu, row'): void {
    this.filterRowEnabled = !this.filterRowEnabled;
    this.onResize(smallFilterMode, largeFilterMode);
  };

  baseGridPrototype.ngOnInit = function (...args: any[]): void {
    this.filterRowEnabled = hasRowFilterMode(this.filterable);
    this.filterable = this.filterRowEnabled ? this.filterable : false;
    originalNgOnInit.apply(this, args);
    normalizeFilterModeByViewport(this);
  };

  baseGridPrototype.ngAfterViewInit = function (...args: any[]): void {
    if (typeof originalNgAfterViewInit === 'function') {
      originalNgAfterViewInit.apply(this, args);
    }

    bindGlobalToolbarFilterButtonCompat();
    bindToolbarFilterButtonCompat(this);
    const container = getGridContainer(this);
    if (container != null) {
      window.setTimeout(() => syncToolbarFilterButtonState(container), 0);
    }
  };

  baseGridPrototype.ngOnChanges = function (...args: any[]): void {
    if (typeof originalNgOnChanges === 'function') {
      originalNgOnChanges.apply(this, args);
    }

    this.filterRowEnabled = hasRowFilterMode(this.filterable) || !!this.filterRowEnabled;
    normalizeFilterModeByViewport(this);
    window.setTimeout(() => syncAllToolbarFilterButtonState(), 0);
  };

  isPatched = true;
}
